<template>
    <div>
        <v-table class="reservation-table elevation-1">
            <thead>
                <tr>
                    <th class="text-left">{{ $t('book_number') }}</th>
                    <th class="text-left">{{ $t('guest_name') }}</th>
                    <th class="text-left">{{ $t('room_type') }}</th>
                    <th class="text-left">{{ $t('check_in_out') }}</th>
                    <th class="text-center">{{ $t('nights') }}</th>
                    <th class="text-center">{{ $t('commission_percentage') }}</th>
                    <th class="text-left">{{ $t('result') }}</th>
                    <th class="text-right">{{ $t('original_amount') }}</th>
                    <th class="text-right">{{ $t('final_amount') }}</th>
                    <th class="text-right">{{ $t('payment_charge') }}</th>
                    <th class="text-right">{{ $t('commission_amount') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="item in reservations" :key="item.bookNumber" class="reservation-row">
                    <td>{{ item.bookNumber }}</td>
                    <td>{{ item.guestName }}</td>
                    <td>{{ formatSustainabilityLevel(item.sustainabilityLevel) }}</td>
                    <td>
                        {{ $filters.dateFormat(item.checkIn) }}<br />
                        {{ $filters.dateFormat(item.checkOut) }}
                    </td>
                    <td class="text-center">{{ item.nights }}</td>
                    <td class="text-center">{{ item.commissionPercentage }}</td>
                    <td>{{ item.result }}</td>
                    <td class="text-right">{{ $filters.priceFormat(item.originalAmount, currency) }}</td>
                    <td class="text-right">{{ $filters.priceFormat(item.finalAmount, currency) }}</td>
                    <td class="text-right">{{ $filters.priceFormat(item.paymentCharge, currency) }}</td>
                    <td class="text-right">{{ $filters.priceFormat(item.commissionAmount, currency) }}</td>
                </tr>
            </tbody>
            <tfoot>
                <tr class="bg-grey-lighten-4 total-row">
                    <td colspan="7" class="text-right font-weight-bold">{{ $t('total') }}:</td>
                    <td class="text-right font-weight-bold">
                        {{ $filters.priceFormat(totalOriginalAmount, currency) }}
                    </td>
                    <td class="text-right font-weight-bold">{{ $filters.priceFormat(totalFinalAmount, currency) }}</td>
                    <td class="text-right font-weight-bold">
                        {{ $filters.priceFormat(totalPaymentCharge, currency) }}
                    </td>
                    <td class="text-right font-weight-bold">
                        {{ $filters.priceFormat(totalCommissionAmount, currency) }}
                    </td>
                </tr>
            </tfoot>
        </v-table>
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue'

    const props = defineProps({
        reservations: {
            type: Array,
            required: true,
            default: () => [],
        },
        currency: {
            type: String,
            default: 'EUR',
        },
    })

    // Format sustainability level for display
    const formatSustainabilityLevel = (level: string) => {
        if (!level) return 'Basic'

        switch (level.toUpperCase()) {
            case 'ZERO':
                return 'Zero'
            case 'SUSTAINABLE':
                return 'Sustainable'
            case 'BASIC':
            default:
                return 'Basic'
        }
    }

    // Calculate totals
    const totalOriginalAmount = computed(() => props.reservations.reduce((sum, item) => sum + item.originalAmount, 0))

    const totalFinalAmount = computed(() => props.reservations.reduce((sum, item) => sum + item.finalAmount, 0))

    const totalPaymentCharge = computed(() => props.reservations.reduce((sum, item) => sum + item.paymentCharge, 0))

    const totalCommissionAmount = computed(() =>
        props.reservations.reduce((sum, item) => sum + item.commissionAmount, 0)
    )

    // Expose totals for parent component
    defineExpose({
        totalOriginalAmount,
        totalFinalAmount,
        totalPaymentCharge,
        totalCommissionAmount,
    })
</script>

<style scoped>
    .reservation-table {
        width: 100%;
        border-collapse: collapse;
    }

    .reservation-table th,
    .reservation-table td {
        padding: 12px 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }

    .reservation-table th {
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
        white-space: nowrap;
    }

    .reservation-table td {
        color: rgba(0, 0, 0, 0.87);
    }

    .reservation-row:hover {
        background-color: rgba(0, 0, 0, 0.04);
    }

    .total-row td {
        padding-top: 16px;
        padding-bottom: 16px;
        font-weight: 500;
    }

    /* Column widths */
    .reservation-table th:nth-child(1),
    .reservation-table td:nth-child(1) {
        min-width: 120px;
    }

    .reservation-table th:nth-child(2),
    .reservation-table td:nth-child(2) {
        min-width: 150px;
    }

    .reservation-table th:nth-child(3),
    .reservation-table td:nth-child(3) {
        min-width: 150px;
    }

    .reservation-table th:nth-child(4),
    .reservation-table td:nth-child(4) {
        min-width: 80px;
    }

    .reservation-table th:nth-child(5),
    .reservation-table td:nth-child(5) {
        min-width: 80px;
    }

    .reservation-table th:nth-child(6),
    .reservation-table td:nth-child(6) {
        min-width: 100px;
    }

    .reservation-table th:nth-child(7),
    .reservation-table td:nth-child(7),
    .reservation-table th:nth-child(8),
    .reservation-table td:nth-child(8),
    .reservation-table th:nth-child(9),
    .reservation-table td:nth-child(9),
    .reservation-table th:nth-child(10),
    .reservation-table td:nth-child(10) {
        min-width: 120px;
    }

    @media print {
        .reservation-table {
            border: none;
        }

        .reservation-table th,
        .reservation-table td {
            padding: 8px;
        }
    }
</style>
